/**
 * Remembers in-flight multipart uploads so a reload does not throw away the
 * bytes already sent.
 *
 * The server has always supported this — `GET /uploads/{id}/parts` reports the
 * parts S3 already holds — but nothing on this side kept the upload id across a
 * page load, so every attempt started a brand-new multipart and re-sent
 * everything. On the transfers this app exists for, that is the difference
 * between a dropped connection costing thirty seconds and costing eight
 * gigabytes.
 *
 * sessionStorage rather than localStorage: an upload belongs to the tab that
 * started it, and a record that outlives the tab would only ever point at an
 * upload the sweeper has since aborted.
 */

const PREFIX = 'ibibik.upload.'

export interface UploadSession {
  fileId: string
  uploadId: string
  /** Both stored so a server-side part-size change invalidates the record —
   *  different boundaries would make the resumed parts unusable. */
  partSize: number
  partCount: number
}

/**
 * Identifies the file well enough to be sure a resumed session belongs to it.
 * Name, size and mtime together are what the browser can offer without reading
 * the contents; a file that differs in none of them is the same file for this
 * purpose.
 */
function key(token: string, sectionId: string, file: File): string {
  return `${PREFIX}${token}:${sectionId}:${file.name}:${file.size}:${file.lastModified}`
}

/** sessionStorage throws in private modes and when the quota is full. Losing a
 *  resume record is never worth failing an upload over. */
function safely<T>(fn: () => T): T | null {
  try {
    return fn()
  } catch {
    return null
  }
}

export function remember(
  token: string,
  sectionId: string,
  file: File,
  session: UploadSession,
): void {
  safely(() => sessionStorage.setItem(key(token, sectionId, file), JSON.stringify(session)))
}

export function recall(token: string, sectionId: string, file: File): UploadSession | null {
  return safely(() => {
    const raw = sessionStorage.getItem(key(token, sectionId, file))
    if (!raw) return null
    const s = JSON.parse(raw) as Partial<UploadSession>
    // Anything malformed is treated as absent — a bad record must not be able
    // to send the uploader somewhere strange.
    if (
      typeof s.fileId !== 'string' ||
      typeof s.uploadId !== 'string' ||
      typeof s.partSize !== 'number' ||
      typeof s.partCount !== 'number'
    ) {
      return null
    }
    return s as UploadSession
  })
}

export function forget(token: string, sectionId: string, file: File): void {
  safely(() => sessionStorage.removeItem(key(token, sectionId, file)))
}
